import { cva } from 'class-variance-authority';

/**
 * Menu class recipes — the action menu ("more actions"), not the select's option list.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-menu-*` slots, which `.ds-menu` sets per theme in `components/styles/design-system.css`.
 * Read the block header there first — three of those slots are EXTRAPOLATED rather than authored,
 * because the drawn `Menu` component is a selection list with no divider and no destructive row.
 *
 * Geometry is read off the `Menu` master (node 556:20624) and matches the select's list exactly,
 * which is expected: Figma draws one `Menu` component, and the select's popup (node 556:20985) and
 * this panel are both instances of it. Its rows are instances of `List-Item` (node 82:7424).
 *
 * Re-read on 2026-09-15 after the `List-Item` rework, which moved every number in both the panel and
 * the row. What changed, and where the values come from, is documented on each recipe below.
 */

/**
 * The panel.
 *
 * `ds-menu` is applied by the component rather than inherited, for the same reason `Select` applies
 * `ds-select`: the popup is portalled into `<body>`, so it has none of the trigger's ancestors and
 * would otherwise resolve every slot to its light-mode fallback. The theme class goes on the
 * `Portal` instead, which IS an ancestor of this element, so `:where(.dark) .ds-menu` still reaches
 * it.
 *
 * Width is content-driven with a 220px floor, and this is the one place the action menu must NOT
 * copy the select. The select pins its panel to `--anchor-width + 2px` so the list lines up with
 * the field it belongs to; an action menu's anchor is a 40px icon button, so borrowing that rule
 * would render a 40px-wide panel. 220px is the row width the component draws (node 556:20713), used
 * here as a minimum rather than a fixed size so a long label sets the width instead of truncating.
 *
 * `max-h-[--available-height]` plus scrolling keeps a long menu on screen near the bottom of the
 * viewport; Base UI computes the value from the collision boundary.
 *
 * ── The geometry, re-read on 2026-09-15 ────────────────────────────────────────────────────────
 *
 * Off the reworked master's bound variables, not measured off a render:
 *
 *   radius    `radius/md`  16px, was 20
 *   padding   `x2` / `x2,5`  8px across, 10px down, was 12 / 16
 *   row gap   `x0,5`  2px, was 0
 *
 * The gap matters more than its size suggests: the rows are pills now, and without air between them
 * two adjacent hover fills would touch and read as one block. Unlike the select's list this one CAN
 * carry the gap on the panel — the rows are direct children here, with no `List` between them.
 *
 * The 220px FLOOR is deliberately unchanged, even though the master's frame is 320 wide. That frame
 * is `layoutSizingHorizontal: 'FILL'`, so 320 is whatever its parent happened to give it rather than
 * a specified width; the `List-Item` master's own frame is 220, which is the number this already
 * used. Pinning the action menu to 320 would widen every shipped menu on the strength of a
 * measurement the file does not actually make.
 */
export const menuPanelRecipe = cva([
  'ds-menu',
  'box-border flex w-max min-w-[220px] flex-col gap-[2px]',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'px-[8px] py-[10px]',
  'rounded-[16px]',
  'border-solid border-[length:1px] border-[var(--ds-menu-border)]',
  'bg-[var(--ds-menu-bg)]',
  'outline-none',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 * The z-index belongs HERE rather than on the panel, because this is the element Base UI gives
 * `position: fixed`; on the panel it would have nothing to stack against.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500) — the
 * same value and the same reasoning as `selectPositionerRecipe`. An action menu inside a legacy MUI
 * `Dialog` is an ordinary case during the refresh, and a popup trapped behind its own dialog is
 * unusable. The value is MUI's scale rather than this layer's because MUI is what it coexists with.
 */
export const menuPositionerRecipe = cva(['z-[1400]']);

/**
 * A row — the reworked Figma `List-Item` (node 82:7424), which this and the select's options share.
 *
 * Figma `Label/lg` — 14px Medium on a 20px line, truncating to one line — with 12px across and 8px
 * down, which is the design's 36px row. It was 6px down and a 32px row; `x2` is what the reworked
 * cells bind. The select's options use the same cell, so the two moved together.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI highlights on
 * hover by default. That is what the cells want: one hover treatment for both. `State=Focus` IS a
 * separate cell, and it is a ring rather than a fill — see the focus block below.
 *
 * There is deliberately no selected state. The drawn component has one — it is a selection list, and
 * the rework gave it back a trailing tick — but an action menu has nothing to select: each row runs a
 * command and the panel closes. `Select` renders that half of the cell; see `selectItemCheckRecipe`.
 *
 * The `Type=Multi Select` half of the cell has no home here either, and that is the one part of the
 * rework not reproduced anywhere in code: it draws a leading checkbox, which needs a multi-select
 * list, and neither component is one. Flagged rather than approximated.
 *
 * The RADIUS is an axis rather than a constant, because a described row is not a taller version of a
 * plain one — see `multiline` below.
 *
 * Selector precedence is kept mutually exclusive rather than order-dependent, as in the sibling
 * recipes: disabled wins over hovered by excluding it, so which rule applies never depends on
 * utility sort order.
 */
export const menuItemRecipe = cva(
  [
    'group/mi',
    'relative flex w-full shrink-0 items-center gap-[8px]',
    'm-0 appearance-none border-0 bg-transparent text-left',
    'px-[12px] py-[8px]',
    'font-sans text-label-lg select-none',
    'outline-none',
    'cursor-pointer',
    'transition-[color,background-color] duration-150 motion-reduce:transition-none',
    /**
     * The keyboard ring — the design system's SHARED two-shadow ring, which `Button`, `Checkbox`
     * and `Radio` all draw: `effect/focus-inner` spread 2 painted over `effect/focus-outer` spread
     * 4, so the inner band reads as a GAP and the visible ring is the outer band it uncovers.
     *
     * Two parts of it are substituted here, both measured, and each one is why this comment is long.
     *
     * ── The GAP is the panel's own fill, not `effect/focus-inner` ───────────────────────────────
     *
     * `effect/focus-inner` carries the two `background/primary` values — `--neutral-50` light,
     * `--neutral-900` dark — because the gap's whole job is to look like the surface the control
     * sits on. That holds for every component shipping this ring so far: they all sit on the page or
     * on a primary surface. `--ds-menu-bg` is `background/secondary`, so naming the role here
     * painted a band of near-white against a grey panel — a visible halo round the focused row,
     * read off the rendered row rather than judged by eye.
     *
     * So the gap reads `--ds-menu-focus-gap`, which the class points at its own panel fill. That is
     * `Radio`'s precedent rather than a new idea: `--ds-radio-focus-backdrop` exists for exactly
     * this reason, because a gap ring needs an opaque backdrop matching whatever it sits against.
     * `Button`'s own note says it plainly — the gap "is what holds the ring off a same-coloured
     * surface", which only works if it IS that surface's colour.
     *
     * This is also why the ring cannot be an offset `outline`, which was tried: an outline's gap is
     * transparent, and while that happens to look right on an opaque panel it cannot follow the
     * row's `rounded-full` with a painted band, and it drops the system's geometry vocabulary for a
     * second technique doing the same job.
     *
     * ── The RING colour is `--border-focus`, not `effect/focus-outer` ──────────────────────────
     *
     * The style's own outer is a 40% neutral, which composites to 2.58:1 against the light panel —
     * under 1.4.11's 3:1 for the thing identifying a focused control. See `--ds-select-item-focus`
     * for the full measurement.
     *
     * Inner listed FIRST so it paints on top of the wider outer one, which is the order
     * `FocusRing.stories` composes and the order every sibling recipe uses. Every length is a token,
     * so the ring cannot drift from the one the rest of the system draws. `outline-none` in the base
     * suppresses the browser's own ring, which would otherwise sit inside this one.
     */
    'focus-visible:shadow-[0_0_0_var(--effect-focus-inner-spread)_var(--ds-menu-focus-gap),0_0_0_var(--effect-focus-outer-spread)_var(--ds-menu-focus)]',

    // Tone-independent. What a disabled row looks like is per tone (see `tone` below), but the
    // pointer says the same thing either way.
    'data-[disabled]:cursor-not-allowed',
  ],
  {
    variants: {
      /**
       * The row's corner radius, which depends on whether it has a description.
       *
       * Design feedback on HNS-364. Both values come from the same rule — the hover fill is a shape
       * NESTED inside the panel, so its radius is the panel's radius minus the gap between the two.
       * With the reworked panel at `rounded-[16px]` and `px-[8px]` that is still 8px, so the
       * described row's value is unchanged even though both inputs to it moved.
       *
       * The single-line row is now `rounded-full`, which is what the reworked cells bind
       * (`radius/full`) rather than a value derived here. It renders identically to the `24px` it
       * replaced — anything over half of 36 clamps to a pill — but it stays right if the row grows,
       * and it says what the cell says.
       *
       * A described row is ~60px (see `menuItemDescriptionRecipe`), so a pill would read as a
       * capsule rather than a row there; 8px is the value that makes its curve concentric with the
       * panel's.
       *
       * An axis rather than a `[&:has([data-part=item-description])]` selector: `Menu` already knows
       * which rows have descriptions, and a CSS-only version would silently do nothing on any
       * browser without `:has`.
       */
      multiline: {
        true: 'rounded-[8px]',
        false: 'rounded-full',
      },
      tone: {
        /**
         * Disabled RECOLOURS here, following the reworked cells, which author
         * `foreground/disabled/default` on the disabled rows rather than dimming them. An explicit
         * disabled colour is also what `contrast.md` asks for: a multiplier is a value no contrast
         * harness can read without compositing it first.
         *
         * The disabled cells keep the resting FILL, so there is no background rule to add — and
         * `data-[disabled]` already beats `data-[highlighted]` by exclusion, so a disabled row
         * cannot pick up a hover fill on the way past.
         */
        default: [
          'text-[var(--ds-menu-item-fg)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-hover)]',
          'data-[highlighted]:not-data-[disabled]:text-[var(--ds-menu-item-fg-hover)]',
          'data-[disabled]:text-[var(--ds-menu-item-fg-disabled)]',
          /**
           * The leading glyph, a step quieter than the label — the reworked cells hold it at
           * `foreground/secondary/default` while the label brightens on hover.
           *
           * Declared HERE rather than in `menuItemIconRecipe` because it is tone-specific: a
           * destructive row's glyph keeps the row's `currentColor`, which is what makes it red
           * without the caller knowing about tones. A descendant selector is what lets one axis
           * own both halves; the icon recipe stays tone-blind and keeps working for either.
           *
           * Dropped on a disabled row, so the glyph follows the label down — the disabled cells
           * draw both at `foreground/disabled/default`, and there the dimness IS the state.
           */
          'not-data-[disabled]:[&_[data-part="item-icon"]]:text-[var(--ds-menu-item-icon)]',
        ],
        /**
         * The destructive row keeps ONE label colour across resting and hovered, unlike a default
         * row's one-step brightening. The FILL is what signals the highlight here: a row tinted red
         * is unmistakably the highlighted one, so moving the label as well buys nothing and makes
         * the single destructive item on a panel read as two different reds as the cursor passes.
         *
         * It also keeps the DIMMING for its disabled state, where the default row now recolours.
         * Recolouring a destructive row to neutral grey would erase the one thing saying the action
         * is dangerous, and no cell exists for "disabled and destructive" anywhere in the library —
         * not in the reworked `List-Item` and not in `Button` — so there is nothing to be 1:1 with.
         * The dimming reads a SLOT rather than an `opacity-50` literal, so the harness can see it.
         */
        destructive: [
          'text-[var(--ds-menu-item-fg-destructive)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-destructive-hover)]',
          'data-[disabled]:opacity-[var(--ds-menu-disabled-opacity)]',
        ],
      },
    },
    defaultVariants: { multiline: false, tone: 'default' },
  }
);

/**
 * A row's leading glyph. Sized and boxed exactly as the select's option icons, so a 16px icon lands
 * the same way in both menus.
 *
 * TONE-BLIND on colour, which is a change in where the colour comes from rather than in what this
 * recipe does. It still resolves `currentColor` by default — the destructive row's glyph is red for
 * exactly that reason — but the `tone: default` axis on `menuItemRecipe` now overrides it to
 * `--ds-menu-item-icon` through a descendant selector, because the reworked cells hold the glyph a
 * step below the label rather than at it. Keeping the override on the AXIS and not here is what
 * lets one rule serve both tones; see the note beside it.
 *
 * The select's `selectItemIconRecipe` names its slot directly, because that component has no tones.
 */
/**
 * Sizes the row's leading glyph.
 *
 * TWO rules, because there are two kinds of glyph here and neither rule reaches both — the same
 * split `buttonIconRecipe` needs (PR #1130), in the Menu's own copy of the defect.
 *
 *   - `[&_svg]:size-full` sizes a RAW svg, which has no font-size relationship to inherit and is
 *     sized by its own `width`/`height` attributes instead.
 *   - `text-[length:16px]` sizes a MUI-backed glyph, i.e. anything out of `icons/`. `SvgIcon` draws
 *     itself at `1em` and the local-svg factory defaults `fontSize="inherit"`, so giving the SLOT a
 *     font-size is what makes that `1em` resolve to 16px.
 *
 * The font-size is the one doing the work. `size-full` cannot size a MUI glyph: it is a Tailwind
 * utility and so sits in `@layer utilities`, while `SvgIcon`'s `1em` is UNLAYERED Emotion — and
 * unlayered beats layered whatever the specificity. Without it, a row icon resolved `1em` against
 * the row's own label type size and drew smaller than the 16px box it sits in.
 *
 * A caller's own `fontSize` / `sx` on the glyph still wins, because a direct declaration beats an
 * inherited one. That is deliberate, and it is why this is safe to change in one place: every call
 * site that pins its glyph keeps drawing exactly what it draws today.
 */
export const menuItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px]',
  '[&_svg]:size-full',
]);

/** The label. `min-w-0` releases the flex item's minimum content size so `truncate` can shrink it. */
export const menuItemLabelRecipe = cva(['min-w-0 truncate']);

/**
 * The column holding the label and, when there is one, the description.
 *
 * Always rendered, even for a single-line row, so the label's box is the same element in both cases
 * and a row cannot pick up different flex behaviour by gaining a description. On a row without one
 * the column collapses to the label's own line box, which is what the 32px cell already was.
 */
export const menuItemTextRecipe = cva(['flex min-w-0 flex-1 flex-col']);

/**
 * The description.
 *
 * EXTRAPOLATED — see `MenuOption.description`. Colour comes from the row through inheritance rather
 * than from a slot of its own, following `Alert`, where the title and the description are both
 * `--ds-alert-fg`.
 *
 * `text-body-md` is the scale's default reading size, chosen by what this line IS — a description —
 * rather than by matching the size it used to draw. It used to be a step smaller than the row's
 * label, on the reasoning that `Alert` separates its two lines by size and weight. The label is a
 * label style at the same size, so the two lines now differ by WEIGHT alone, which is the other
 * half of what `Alert` does and is what the library specifies for this line.
 *
 * The line box is unchanged, so a single-line description does not move the row. A description long
 * enough to WRAP is a different matter: it wraps inside the cap below, so a larger size can push it
 * onto another line and grow the row. That is accepted rather than worked around — nothing pins this
 * row's height, and the story that measures it asserts a floor.
 *
 * `max-w-[280px]` is what makes the line WRAP. The panel is `w-max`, so without a concrete width
 * the description's max-content width would simply widen the panel until the whole sentence fit on
 * one line — a ~70-character line producing a ~450px popover. 280px is a chosen number, not a
 * measured one: the drawn row is 220px (node 556:20713), and this is the next step up that reads as
 * a menu rather than a dialog. It applies here rather than on the panel so a menu WITHOUT
 * descriptions keeps sizing to its content exactly as before.
 */
export const menuItemDescriptionRecipe = cva(['text-body-md', 'max-w-[280px] whitespace-normal']);

/**
 * The group separator.
 *
 * EXTRAPOLATED — see the `.ds-menu` block header. Rendered as a bordered `<div>` rather than an
 * `<hr>`: Base UI's `Menu.Separator` sets `role="separator"` itself, and a UA-styled `<hr>` would
 * bring its own margins into a panel whose spacing is already specified.
 *
 * The negative inset is what makes the line span the full panel rather than stopping inside the
 * rows' padding — the legacy menu uses MUI's `variant="middle"` Divider, which insets it. The design
 * draws no separator at all, so this follows the panel's own border, which runs edge to edge.
 *
 * ── The extrapolation has since been CONFIRMED from elsewhere (2026-09-17) ──────────────────────
 *
 * The `Menu` set still draws no separator, so the note above stands. But the library has since
 * published a `Divider` component (node 1472:150575, ported to `components/Divider`), and its
 * description specifies this composition explicitly: full-bleed, "runs edge to edge rather than
 * inset", and "in Menu's Content slot: the separator sits above the row that OPENS a group, never
 * below the row that closes one, and never against the panel's own top padding". Both readings this
 * file arrived at independently — the negative inset and `separatorBefore`'s direction — are what
 * the design now says.
 *
 * What is NOT yet reconciled is the colour. The divider takes the translucent `--border-on-surface`
 * so one rule works on any surface; this slot takes `--border-secondary`, which is opaque and tuned
 * to the panel's own fill. Both measure inside the decorative band on this panel, so neither is
 * wrong today and nothing here changes on that basis — but if this separator is ever replaced by
 * the `Divider` component, that is the difference to expect.
 *
 * It MUST equal the panel's own horizontal padding, and that is a hard constraint rather than a
 * matter of taste. This read `-mx-[12px]` against the panel's old `px-[12px]`; the `List-Item`
 * rework took the panel to `px-[8px]` and left this at 12, so the line overhung the padding box by
 * 4px a side. The panel is `overflow-y-auto`, and CSS resolves a `visible` overflow on one axis to
 * `auto` when the other is not — so the overhang gave the panel a real horizontal scrollbar, whose
 * height then pushed the content past the panel's own height and produced a VERTICAL scrollbar
 * beside it. Two scrollbars on a 151px menu, from one stale number.
 *
 * `my-[8px]` (`x2`) is the group break. Deliberately larger than the 2px row gap, so a break
 * between groups reads as heavier than the air between two rows in one group.
 */
export const menuSeparatorRecipe = cva([
  '-mx-[8px] my-[8px] h-0 shrink-0',
  'border-0 border-t border-solid border-[var(--ds-menu-separator)]',
]);
